import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { PartUrlDto } from '../api/models';
import { ApiProvider } from '../api.provider';

export interface UploadProgress {
  /** 0..1 over the whole file. */
  fraction: number;
  uploadedBytes: number;
  totalBytes: number;
  partsDone: number;
  partsTotal: number;
  /** Bytes per second over the last few parts, or null before the first one lands. */
  bytesPerSecond: number | null;
  secondsRemaining: number | null;
}

/** How many part URLs to presign per round-trip. */
const URL_BATCH = 20;
/** Parts uploaded at once. Enough to saturate a connection, few enough to keep memory flat. */
const CONCURRENCY = 3;
const MAX_PART_ATTEMPTS = 4;

export class UploadCancelled extends Error {
  constructor() {
    super('Upload cancelled');
  }
}

/**
 * Multipart upload straight from the browser to S3.
 *
 * A match is several GB over a connection that will drop at least once, so the
 * unit of failure has to be a part and not the file: a part that fails is
 * retried on its own, and the rest of the upload never restarts. That is the
 * whole reason this is not a single presigned PUT.
 */
@Injectable({ providedIn: 'root' })
export class UploadService {
  readonly progress$ = new BehaviorSubject<UploadProgress | null>(null);
  private cancelled = false;

  constructor(private readonly api: ApiProvider) {}

  cancel() {
    this.cancelled = true;
  }

  async upload(matchVideoUuid: string, file: File): Promise<void> {
    this.cancelled = false;

    const { partSizeBytes } = await firstValueFrom(
      this.api.matchVideo.matchVideoControllerStartUpload({
        uuid: matchVideoUuid,
        body: { fileName: file.name, size: file.size },
      }),
    );

    const partsTotal = Math.ceil(file.size / partSizeBytes);
    const etags: { partNumber: number; etag: string }[] = [];
    let uploadedBytes = 0;
    const startedAt = Date.now();

    const report = () => {
      const elapsed = (Date.now() - startedAt) / 1000;
      const bytesPerSecond = elapsed > 1 ? uploadedBytes / elapsed : null;
      this.progress$.next({
        fraction: file.size ? uploadedBytes / file.size : 0,
        uploadedBytes,
        totalBytes: file.size,
        partsDone: etags.length,
        partsTotal,
        bytesPerSecond,
        secondsRemaining: bytesPerSecond ? Math.round((file.size - uploadedBytes) / bytesPerSecond) : null,
      });
    };
    report();

    try {
      for (let first = 1; first <= partsTotal; first += URL_BATCH) {
        this.throwIfCancelled();

        const partNumbers: number[] = [];
        for (let n = first; n < first + URL_BATCH && n <= partsTotal; n++) partNumbers.push(n);

        // Presigned part URLs expire; asking for them in batches keeps the last
        // batch of a long upload from being signed an hour before it is used.
        const urls = await firstValueFrom(
          this.api.matchVideo.matchVideoControllerGetPartUrls({ uuid: matchVideoUuid, body: { partNumbers } }),
        );

        for (let i = 0; i < urls.length; i += CONCURRENCY) {
          this.throwIfCancelled();
          const slice = urls.slice(i, i + CONCURRENCY);
          const results = await Promise.all(
            slice.map(async (part) => {
              const blob = this.sliceFor(file, part.partNumber, partSizeBytes);
              const etag = await this.putPart(part, blob);
              uploadedBytes += blob.size;
              report();
              return { partNumber: part.partNumber, etag };
            }),
          );
          etags.push(...results);
        }
      }

      await firstValueFrom(
        this.api.matchVideo.matchVideoControllerCompleteUpload({ uuid: matchVideoUuid, body: { parts: etags } }),
      );
      this.progress$.next(null);
    } catch (error) {
      // Leaving a multipart upload open means S3 keeps billing for its parts,
      // so abort on the way out even when the failure was the user cancelling.
      await firstValueFrom(this.api.matchVideo.matchVideoControllerAbortUpload({ uuid: matchVideoUuid })).catch(() => undefined);
      this.progress$.next(null);
      throw error;
    }
  }

  private sliceFor(file: File, partNumber: number, partSizeBytes: number): Blob {
    const start = (partNumber - 1) * partSizeBytes;
    return file.slice(start, Math.min(start + partSizeBytes, file.size));
  }

  /**
   * PUTs one part with XMLHttpRequest rather than fetch: the ETag comes back in
   * a response header, and `fetch` cannot read it cross-origin unless S3 exposes
   * it in CORS — which OVH does not do by default.
   */
  private putPart(part: PartUrlDto, blob: Blob): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      let attempt = 0;
      const tryOnce = () => {
        attempt++;
        if (this.cancelled) return reject(new UploadCancelled());

        const xhr = new XMLHttpRequest();
        xhr.open('PUT', part.url, true);
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            const etag = xhr.getResponseHeader('ETag');
            if (etag) return resolve(etag);
            return reject(new Error(`S3 did not return an ETag for part ${part.partNumber}; check the bucket CORS ExposeHeaders`));
          }
          retryOrFail(new Error(`Part ${part.partNumber} failed with HTTP ${xhr.status}`));
        };
        xhr.onerror = () => retryOrFail(new Error(`Part ${part.partNumber} failed: network error`));
        xhr.ontimeout = () => retryOrFail(new Error(`Part ${part.partNumber} timed out`));
        xhr.send(blob);
      };

      const retryOrFail = (error: Error) => {
        if (attempt >= MAX_PART_ATTEMPTS || this.cancelled) return reject(error);
        setTimeout(tryOnce, 1000 * attempt);
      };

      tryOnce();
    });
  }

  private throwIfCancelled() {
    if (this.cancelled) throw new UploadCancelled();
  }
}
